// src/components/settings/SecurityTab.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../api/axios';
import { getProfile, sendVerify } from '../../services/userService';
import { toast } from 'react-toastify';

const MIN_LEN = 8;
const hasLower = (s) => /[a-z]/.test(s || '');
const hasUpper = (s) => /[A-Z]/.test(s || '');
const hasNumber = (s) => /[0-9]/.test(s || '');

export default function SecurityTab() {
  // Profile (for verify banner)
  const [user, setUser] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Verify email
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Change password form
  const [form, setForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [caps, setCaps] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load profile (fast from cache, then fresh)
  useEffect(() => {
    try {
      const cached = localStorage.getItem('me');
      if (cached) setUser(JSON.parse(cached));
    } catch {}
    (async () => {
      setLoadingProfile(true);
      try {
        const me = await getProfile();
        setUser(me);
        localStorage.setItem('me', JSON.stringify(me));
      } catch {
        // Profile is optional for this tab; errors are handled on Profile page too.
      } finally {
        setLoadingProfile(false);
      }
    })();
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (!cooldown) return;
    const id = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const checks = useMemo(() => {
    const pwd = form.newPassword;
    return {
      len: (pwd || '').length >= MIN_LEN,
      lower: hasLower(pwd),
      upper: hasUpper(pwd),
      num: hasNumber(pwd),
      match: !!pwd && pwd === form.confirmPassword,
    };
  }, [form.newPassword, form.confirmPassword]);

  const canSubmit =
    !!form.oldPassword &&
    checks.len &&
    checks.lower &&
    checks.upper &&
    checks.num &&
    checks.match &&
    !loading;

  function onChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function onSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;

    setLoading(true);
    try {
      await axios.put('/auth/change-password', {
        oldPassword: form.oldPassword,
        newPassword: form.newPassword,
        confirmPassword: form.confirmPassword,
      });
      toast.success('Password changed successfully.');
      setForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      if (err?._status === 400 || err?._is422) {
        toast.error(err?._data?.message || 'Validation error. Please check your inputs.');
      } else if (err?._is429) {
        const after = err?._retryAfter ? ` Try again in ~${err._retryAfter}s.` : '';
        toast.warning(`Too many attempts.${after}`);
      } else if (err?._auth) {
        toast.error('Session expired. Please sign in again.');
      } else {
        toast.error('Failed to change password.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function resendVerification() {
    if (!user || user.emailVerifiedAt || resending || cooldown > 0) return;
    setResending(true);
    try {
      await sendVerify();
      toast.success('Verification email sent.');
      setCooldown(30); // UI cooldown; server also rate-limits
    } catch (err) {
      if (err?._is429) {
        const after = err?._retryAfter ? ` Try again in ~${err._retryAfter}s.` : '';
        toast.warning(`You’re sending verification emails too quickly.${after}`);
        setCooldown(Math.max(30, Number(err?._retryAfter || 0)));
      } else if (err?._auth) {
        toast.error('Session expired. Please sign in again.');
      } else {
        toast.error('Could not resend verification email.');
      }
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="space-y-8 max-w-2xl">
      {/* Email verification banner (only if unverified) */}
      {!loadingProfile && user && !user.emailVerifiedAt && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-amber-800">
              <b>Verify your email:</b> We sent a link to <b>{user.email}</b>. Didn’t get it?
            </div>
            <button
              onClick={resendVerification}
              disabled={resending || cooldown > 0}
              className="px-3 py-1.5 rounded bg-black text-white text-sm disabled:opacity-60"
            >
              {resending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend'}
            </button>
          </div>
        </div>
      )}

      {/* Change Password */}
      <form onSubmit={onSubmit} className="bg-white border rounded-lg shadow p-6 space-y-4">
        <h2 className="text-lg font-semibold">Change password</h2>

        <div>
          <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700">
            Current password
          </label>
          <input
            id="oldPassword"
            name="oldPassword"
            type="password"
            value={form.oldPassword}
            onChange={onChange}
            placeholder="Current password"
            autoComplete="current-password"
            className="mt-1 border px-3 py-2 rounded w-full focus:outline-none focus:border-gray-500"
            required
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
              New password
            </label>
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              value={form.newPassword}
              onChange={onChange}
              onKeyUp={(e) => setCaps(e.getModifierState && e.getModifierState('CapsLock'))}
              placeholder="New password"
              autoComplete="new-password"
              className="mt-1 border px-3 py-2 rounded w-full focus:outline-none focus:border-gray-500"
              required
            />
            {caps && <p className="mt-1 text-xs text-amber-600">Caps Lock is on.</p>}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm new password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={form.confirmPassword}
              onChange={onChange}
              placeholder="Confirm new password"
              autoComplete="new-password"
              className={`mt-1 border px-3 py-2 rounded w-full focus:outline-none ${
                form.confirmPassword && !checks.match ? 'border-red-300' : 'focus:border-gray-500'
              }`}
              required
            />
            {form.confirmPassword && !checks.match && (
              <p className="mt-1 text-xs text-red-600">Passwords do not match.</p>
            )}
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded p-3">
          <p className="text-xs font-medium text-gray-700 mb-2">Password must include:</p>
          <ul className="grid grid-cols-2 gap-y-1 text-sm">
            <Requirement ok={checks.len} label={`At least ${MIN_LEN} characters`} />
            <Requirement ok={checks.upper} label="An uppercase letter" />
            <Requirement ok={checks.lower} label="A lowercase letter" />
            <Requirement ok={checks.num} label="A number" />
          </ul>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full py-2 rounded-lg font-semibold bg-black text-white disabled:opacity-60"
        >
          {loading ? 'Updating…' : 'Update password'}
        </button>
      </form>

      {/* Two-Factor Authentication placeholder */}
      <div className="bg-white border rounded-lg shadow p-6 space-y-2">
        <h2 className="text-lg font-semibold">Two-Factor Authentication</h2>
        <label className="flex items-center space-x-3">
          <input type="checkbox" disabled className="w-5 h-5" />
          <span className="text-gray-600">Enable 2FA (coming soon)</span>
        </label>
      </div>
    </div>
  );
}

function Requirement({ ok, label }) {
  return (
    <li className={`text-sm ${ok ? 'text-green-600' : 'text-gray-500'}`}>
      <span className={`inline-block w-4 text-center mr-2 ${ok ? '' : 'opacity-60'}`}>
        {ok ? '✓' : '•'}
      </span>
      {label}
    </li>
  );
}
